import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useCurrentEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { computePosition, autoUpdate, offset, flip, shift } from '@floating-ui/dom';
import { Link as LinkIcon, ChevronDown } from 'lucide-react';
import { createLinkedTextInsertContent } from '../extensions/tiptapShared';
import { insertLinkStub } from '../utils/markdownFormatting';
import type { MarkdownSourceContext } from './Toolbar';
import { OPEN_LINK_POPOVER_EVENT, onEditorEvent, offEditorEvent } from '../extensions/globalShortcuts';
import { ToolbarTooltip } from './ToolbarTooltip';
import styles from './Toolbar.module.css';

export const LINK_SHORTCUT = ['mod', 'K'];

function insertLinkedFragment(
  editor: Editor,
  fragment: ReturnType<typeof createLinkedTextInsertContent>,
  opts: { replaceRange?: { from: number; to: number } | null }
) {
  if (opts.replaceRange) {
    editor
      .chain()
      .focus()
      .setTextSelection({ from: opts.replaceRange.from, to: opts.replaceRange.to })
      .deleteSelection()
      .insertContent(fragment)
      .run();
    return;
  }
  editor.chain().focus().insertContent(fragment).run();
}

function focusUrlInputAtEnd(input: HTMLInputElement | null, value: string) {
  if (!input) return;
  input.value = value;
  input.focus();
  input.setSelectionRange(value.length, value.length);
}

function insertMarkdownLinkStub(markdownSource: MarkdownSourceContext) {
  const text = markdownSource.getValue();
  const { start, end } = markdownSource.getSelection();
  const result = insertLinkStub(text, start, end);
  markdownSource.apply(result.text, result.start, result.end);
}

export const LinkToolbarPopover = ({
  variant,
  markdownSource = null
}: {
  variant?: 'bottom' | 'bubble';
  markdownSource?: MarkdownSourceContext | null;
}) => {
  const { editor } = useCurrentEditor();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('https://');
  const [label, setLabel] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const selectionRef = useRef<{ from: number; to: number } | null>(null);
  const linkRangeRef = useRef<{ from: number; to: number } | null>(null);
  const openedAsLinkEditRef = useRef(false);
  const isBubble = variant === 'bubble';

  useEffect(() => {
    if (!editor) return;
    const openFromShortcut = () => {
      const empty = editor.state.selection.empty;
      if (isBubble && empty) return;
      if (!isBubble && !empty) return;
      setOpen(true);
    };
    onEditorEvent(editor, OPEN_LINK_POPOVER_EVENT, openFromShortcut);
    return () => offEditorEvent(editor, OPEN_LINK_POPOVER_EVENT, openFromShortcut);
  }, [editor, isBubble]);

  useEffect(() => {
    if (!open) return;
    const isInsideDropdown = (e: MouseEvent) => {
      const root = ref.current;
      if (!root) return false;
      const t = e.target as Node | null;
      if (t && root.contains(t)) return true;
      const path = e.composedPath?.() ?? [];
      return path.some(n => n === root || (n instanceof Node && root.contains(n)));
    };
    const onDown = (e: MouseEvent) => {
      if (!isInsideDropdown(e)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const trigger = ref.current?.querySelector('button');
    const panel = ref.current?.querySelector(`.${styles.linkPanel}`) as HTMLElement | null;
    if (!trigger || !panel) return;

    const updatePosition = () => {
      computePosition(trigger, panel, {
        placement: isBubble ? 'bottom' : 'top-end',
        strategy: 'fixed',
        middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })]
      }).then(({ x, y, strategy }) => {
        Object.assign(panel.style, {
          position: strategy,
          left: `${x}px`,
          top: `${y}px`,
          right: 'auto',
          bottom: 'auto'
        });
      });
    };

    updatePosition();
    return autoUpdate(trigger, panel, updatePosition, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: true
    });
  }, [open, isBubble]);

  useLayoutEffect(() => {
    if (!open || !editor) return;
    const snap = editor.state.selection;
    selectionRef.current = { from: snap.from, to: snap.to };
    linkRangeRef.current = null;
    openedAsLinkEditRef.current = false;

    if (editor.isActive('link')) {
      openedAsLinkEditRef.current = true;
      const href = editor.getAttributes('link').href || 'https://';
      setUrl(href);
      editor.chain().focus().extendMarkRange('link').run();
      const { from, to } = editor.state.selection;
      linkRangeRef.current = { from, to };
      setLabel(editor.state.doc.textBetween(from, to, ''));
      editor.commands.setTextSelection({ from: snap.from, to: snap.to });
      focusUrlInputAtEnd(urlInputRef.current, href);
    } else {
      setUrl('https://');
      setLabel(editor.state.doc.textBetween(snap.from, snap.to, ''));
      focusUrlInputAtEnd(urlInputRef.current, 'https://');
    }
  }, [open, editor]);

  const apply = useCallback(() => {
    if (!editor) return;
    const href = url.trim();
    if (!href) return;

    const displayText = (label.trim() || href).replace(/\s+/g, ' ');
    const fragment = createLinkedTextInsertContent(href, displayText);
    const saved = selectionRef.current;
    const linkRange = linkRangeRef.current;
    const editingLink = Boolean(openedAsLinkEditRef.current && linkRange);

    if (editingLink && linkRange) {
      insertLinkedFragment(editor, fragment, { replaceRange: linkRange });
    } else if (saved && saved.from !== saved.to) {
      insertLinkedFragment(editor, fragment, { replaceRange: saved });
    } else {
      insertLinkedFragment(editor, fragment, {});
    }
    setOpen(false);
  }, [editor, url, label]);

  const removeLink = () => {
    if (!editor) return;
    const linkRange = linkRangeRef.current;
    if (linkRange) {
      editor
        .chain()
        .focus()
        .setTextSelection({ from: linkRange.from, to: linkRange.to })
        .unsetLink()
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setOpen(false);
  };

  if (markdownSource) {
    return (
      <button
        type="button"
        className={styles.button}
        onMouseDown={e => e.preventDefault()}
        onClick={() => insertMarkdownLinkStub(markdownSource)}
        title="Link"
      >
        <LinkIcon size={14} />
      </button>
    );
  }

  if (!editor) return null;

  const inLink = editor.isActive('link');

  return (
    <div
      ref={ref}
      className={styles.dropdown}
      onMouseDown={e => e.nativeEvent.stopPropagation()}
      onPointerDown={e => e.nativeEvent.stopPropagation()}
    >
      <ToolbarTooltip label="Link" shortcut={LINK_SHORTCUT}>
        <button
          type="button"
          className={
            isBubble
              ? `${styles.bubbleLinkButton} ${inLink ? styles.active : ''}`
              : `${styles.button} ${styles.dropdownTrigger} ${inLink ? styles.active : ''}`
          }
          onClick={() => setOpen(o => !o)}
        >
          <LinkIcon size={isBubble ? 13 : 16} />
          {!isBubble && <ChevronDown size={12} className={styles.chevron} />}
        </button>
      </ToolbarTooltip>
      {open && (
        <div
          className={`${styles.popoverSurface} ${styles.popoverSurfaceRaised} ${styles.linkPanel} ${variant === 'bottom' ? styles.linkPanelBottom : ''}`}
        >
          <label className={styles.linkField}>
            <span>URL</span>
            <input
              ref={urlInputRef}
              type="url"
              className={styles.linkInput}
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  apply();
                }
              }}
              placeholder="https://"
              autoComplete="off"
            />
          </label>
          <label className={styles.linkField}>
            <span>Link text</span>
            <input
              type="text"
              className={styles.linkInput}
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  apply();
                }
              }}
              placeholder="Visible label"
              autoComplete="off"
            />
          </label>
          {inLink && (
            <p className={styles.linkHint}>
              Edit URL or label, then Apply to update the link in the editor.
            </p>
          )}
          <div className={styles.linkActions}>
            {inLink && (
              <button type="button" className={styles.linkRemoveBtn} onClick={removeLink}>
                Remove link
              </button>
            )}
            <button type="button" className={styles.linkApplyBtn} onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
